// Watch it build: the friendly step groups, the App card and the live status line of a Chat that builds an App
// (and the canvas tabs, the building view and Select in the App canvas). Namespace `build-watch`; strings: DES2
// watch-it-build-24.json (24 languages). Placeholder: {text}, the selected part's own text.
// The step verbs carry no ellipsis: the status line appends it. Counts use formatCount nouns ("one:...|other:...").
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  stepListing: 'Looking through files',
  stepReading: 'Reading a file',
  stepWriting: 'Writing a file',
  stepRunning: 'Running a command',
  stepBuilding: 'Building',
  stepOpeningPreview: 'Opening the preview',
  stepCheckingLook: 'Checking how it looks',
  groupBuilt: 'Built the App',
  groupUpdated: 'Updated the App',
  groupBuiltDeck: 'Built the Deck',
  groupUpdatedDeck: 'Updated the Deck',
  groupChecked: 'Checked how it looks',
  groupLookedAt: 'Looked at {domain}',
  groupExplored: 'Explored the project',
  stepsNoun: 'one:step|other:steps',
  failedStepsNoun: 'one:step didn’t work|other:steps didn’t work',
  cardBuilding: 'Building…',
  cardUpdating: 'Updating…',
  cardUpdatedJustNow: 'Updated just now',
  notMounted:
    'This build didn’t show anything: its code doesn’t start the App on the page yet. Chat can fix it.',
  yourApp: 'Your App',
  yourDeck: 'Your Deck',
  tabApp: 'App',
  tabActivity: 'Activity',
  openFullPage: 'Open full page',
  selectToggle: 'Select',
  selectHint: 'Click a part of the App to change it in chat · Esc to stop',
  selectedText: 'Selected: “{text}”',
  selectedPart: 'Selected: a part of the App',
  removeSelection: 'Remove the selection',
  buildingHint: 'Your App appears here as soon as it builds.',
  deckExport: 'Export',
  deckExporting: 'Exporting…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Every slide as a page, exactly as designed.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Each slide as a picture, with its speaker notes. The text can’t be edited.',
  deckExportFailed: 'The deck couldn’t be exported. Try again.',
};
export type BuildWatchCopyKey = keyof typeof en;
type Copy = Record<BuildWatchCopyKey, string>;

const sv: Copy = {
  stepListing: 'Går igenom filer',
  stepReading: 'Läser en fil',
  stepWriting: 'Skriver en fil',
  stepRunning: 'Kör ett kommando',
  stepBuilding: 'Bygger',
  stepOpeningPreview: 'Öppnar förhandsvisningen',
  stepCheckingLook: 'Kontrollerar hur det ser ut',
  groupBuilt: 'Byggde appen',
  groupUpdated: 'Uppdaterade appen',
  groupBuiltDeck: 'Byggde presentationen',
  groupUpdatedDeck: 'Uppdaterade presentationen',
  groupChecked: 'Kontrollerade hur det ser ut',
  groupLookedAt: 'Tittade på {domain}',
  groupExplored: 'Utforskade projektet',
  stepsNoun: 'one:steg|other:steg',
  failedStepsNoun: 'one:steg fungerade inte|other:steg fungerade inte',
  cardBuilding: 'Bygger…',
  cardUpdating: 'Uppdaterar…',
  cardUpdatedJustNow: 'Uppdaterad nyss',
  notMounted:
    'Den här versionen visade ingenting: koden startar ännu inte appen på sidan. Chatten kan rätta det.',
  yourApp: 'Din app',
  yourDeck: 'Din presentation',
  tabApp: 'App',
  tabActivity: 'Aktivitet',
  openFullPage: 'Öppna helsida',
  selectToggle: 'Välj',
  selectHint: 'Klicka på en del av appen för att ändra den i chatten · Esc för att sluta',
  selectedText: 'Valt: ”{text}”',
  selectedPart: 'Valt: en del av appen',
  removeSelection: 'Ta bort markeringen',
  buildingHint: 'Appen visas här så snart den har byggts.',
  deckExport: 'Exportera',
  deckExporting: 'Exporterar…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Varje bild som en sida, precis som den är formgiven.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Varje bild som en bildfil, med talaranteckningar. Texten går inte att redigera.',
  deckExportFailed: 'Presentationen kunde inte exporteras. Försök igen.',
};
const da: Copy = {
  stepListing: 'Gennemgår filer',
  stepReading: 'Læser en fil',
  stepWriting: 'Skriver en fil',
  stepRunning: 'Kører en kommando',
  stepBuilding: 'Bygger',
  stepOpeningPreview: 'Åbner forhåndsvisningen',
  stepCheckingLook: 'Tjekker, hvordan det ser ud',
  groupBuilt: 'Byggede appen',
  groupUpdated: 'Opdaterede appen',
  groupBuiltDeck: 'Byggede præsentationen',
  groupUpdatedDeck: 'Opdaterede præsentationen',
  groupChecked: 'Tjekkede, hvordan det ser ud',
  groupLookedAt: 'Kiggede på {domain}',
  groupExplored: 'Udforskede projektet',
  stepsNoun: 'one:trin|other:trin',
  failedStepsNoun: 'one:trin virkede ikke|other:trin virkede ikke',
  cardBuilding: 'Bygger…',
  cardUpdating: 'Opdaterer…',
  cardUpdatedJustNow: 'Opdateret lige nu',
  notMounted:
    'Denne version viste ingenting: dens kode starter endnu ikke appen på siden. Chatten kan rette det.',
  yourApp: 'Din app',
  yourDeck: 'Din præsentation',
  tabApp: 'App',
  tabActivity: 'Aktivitet',
  openFullPage: 'Åbn hel side',
  selectToggle: 'Vælg',
  selectHint: 'Klik på en del af appen for at ændre den i chatten · Esc for at stoppe',
  selectedText: 'Valgt: »{text}«',
  selectedPart: 'Valgt: en del af appen',
  removeSelection: 'Fjern markeringen',
  buildingHint: 'Din app vises her, så snart den er bygget.',
  deckExport: 'Eksportér',
  deckExporting: 'Eksporterer…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Hvert dias som en side, præcis som det er designet.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Hvert dias som et billede med talernoter. Teksten kan ikke redigeres.',
  deckExportFailed: 'Præsentationen kunne ikke eksporteres. Prøv igen.',
};
const de: Copy = {
  stepListing: 'Dateien werden durchsucht',
  stepReading: 'Datei wird gelesen',
  stepWriting: 'Datei wird geschrieben',
  stepRunning: 'Befehl wird ausgeführt',
  stepBuilding: 'Wird gebaut',
  stepOpeningPreview: 'Vorschau wird geöffnet',
  stepCheckingLook: 'Aussehen wird geprüft',
  groupBuilt: 'App gebaut',
  groupUpdated: 'App aktualisiert',
  groupBuiltDeck: 'Präsentation erstellt',
  groupUpdatedDeck: 'Präsentation aktualisiert',
  groupChecked: 'Geprüft, wie es aussieht',
  groupLookedAt: 'Angesehen: {domain}',
  groupExplored: 'Projekt erkundet',
  stepsNoun: 'one:Schritt|other:Schritte',
  failedStepsNoun: 'one:Schritt hat nicht funktioniert|other:Schritte haben nicht funktioniert',
  cardBuilding: 'Wird gebaut…',
  cardUpdating: 'Wird aktualisiert…',
  cardUpdatedJustNow: 'Gerade aktualisiert',
  notMounted:
    'Dieser Build hat nichts angezeigt: Sein Code startet die App noch nicht auf der Seite. Der Chat kann das beheben.',
  yourApp: 'Ihre App',
  yourDeck: 'Ihre Präsentation',
  tabApp: 'App',
  tabActivity: 'Aktivität',
  openFullPage: 'Ganze Seite öffnen',
  selectToggle: 'Auswählen',
  selectHint: 'Klicken Sie auf einen Teil der App, um ihn im Chat zu ändern · Esc zum Beenden',
  selectedText: 'Ausgewählt: „{text}“',
  selectedPart: 'Ausgewählt: ein Teil der App',
  removeSelection: 'Auswahl entfernen',
  buildingHint: 'Ihre App wird hier angezeigt, sobald sie erstellt wurde.',
  deckExport: 'Exportieren',
  deckExporting: 'Wird exportiert…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Jede Folie als eine Seite, genau wie gestaltet.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Jede Folie als Bild, mit ihren Sprechernotizen. Der Text ist nicht bearbeitbar.',
  deckExportFailed:
    'Die Präsentation konnte nicht exportiert werden. Versuchen Sie es noch einmal.',
};
const fr: Copy = {
  stepListing: 'Parcours des fichiers',
  stepReading: 'Lecture d’un fichier',
  stepWriting: 'Écriture d’un fichier',
  stepRunning: 'Exécution d’une commande',
  stepBuilding: 'Construction',
  stepOpeningPreview: 'Ouverture de l’aperçu',
  stepCheckingLook: 'Vérification du rendu',
  groupBuilt: 'App construite',
  groupUpdated: 'App mise à jour',
  groupBuiltDeck: 'Présentation construite',
  groupUpdatedDeck: 'Présentation mise à jour',
  groupChecked: 'Rendu vérifié',
  groupLookedAt: 'Consulté : {domain}',
  groupExplored: 'Projet exploré',
  stepsNoun: 'one:étape|other:étapes',
  failedStepsNoun: 'one:étape n’a pas fonctionné|other:étapes n’ont pas fonctionné',
  cardBuilding: 'Construction…',
  cardUpdating: 'Mise à jour…',
  cardUpdatedJustNow: 'Mise à jour à l’instant',
  notMounted:
    'Cette version n’a rien affiché : son code ne lance pas encore l’app sur la page. La conversation peut le corriger.',
  yourApp: 'Votre app',
  yourDeck: 'Votre présentation',
  tabApp: 'App',
  tabActivity: 'Activité',
  openFullPage: 'Ouvrir en pleine page',
  selectToggle: 'Sélectionner',
  selectHint: 'Cliquez sur une partie de l’app pour la modifier dans le chat · Échap pour arrêter',
  selectedText: 'Sélection : « {text} »',
  selectedPart: 'Sélection : une partie de l’app',
  removeSelection: 'Retirer la sélection',
  buildingHint: 'Votre app apparaît ici dès qu’elle est construite.',
  deckExport: 'Exporter',
  deckExporting: 'Exportation…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Chaque diapositive sur une page, exactement comme conçue.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Chaque diapositive en image, avec ses notes du présentateur. Le texte n’est pas modifiable.',
  deckExportFailed: 'Impossible d’exporter la présentation. Réessayez.',
};
const es: Copy = {
  stepListing: 'Revisando archivos',
  stepReading: 'Leyendo un archivo',
  stepWriting: 'Escribiendo un archivo',
  stepRunning: 'Ejecutando un comando',
  stepBuilding: 'Construyendo',
  stepOpeningPreview: 'Abriendo la vista previa',
  stepCheckingLook: 'Comprobando cómo se ve',
  groupBuilt: 'App creada',
  groupUpdated: 'App actualizada',
  groupBuiltDeck: 'Presentación creada',
  groupUpdatedDeck: 'Presentación actualizada',
  groupChecked: 'Comprobado cómo se ve',
  groupLookedAt: 'Consultado: {domain}',
  groupExplored: 'Proyecto explorado',
  stepsNoun: 'one:paso|other:pasos',
  failedStepsNoun: 'one:paso no funcionó|other:pasos no funcionaron',
  cardBuilding: 'Construyendo…',
  cardUpdating: 'Actualizando…',
  cardUpdatedJustNow: 'Actualizada hace un momento',
  notMounted:
    'Esta versión no mostró nada: su código aún no inicia la app en la página. El chat puede arreglarlo.',
  yourApp: 'Tu app',
  yourDeck: 'Tu presentación',
  tabApp: 'App',
  tabActivity: 'Actividad',
  openFullPage: 'Abrir a página completa',
  selectToggle: 'Seleccionar',
  selectHint: 'Haz clic en una parte de la app para cambiarla en el chat · Esc para salir',
  selectedText: 'Seleccionado: «{text}»',
  selectedPart: 'Seleccionado: una parte de la app',
  removeSelection: 'Quitar la selección',
  buildingHint: 'Tu app aparece aquí en cuanto se construye.',
  deckExport: 'Exportar',
  deckExporting: 'Exportando…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Cada diapositiva como una página, tal como está diseñada.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Cada diapositiva como imagen, con sus notas del orador. El texto no se puede editar.',
  deckExportFailed: 'No se pudo exportar la presentación. Inténtalo de nuevo.',
};
const it: Copy = {
  stepListing: 'Consultazione dei file',
  stepReading: 'Lettura di un file',
  stepWriting: 'Scrittura di un file',
  stepRunning: 'Esecuzione di un comando',
  stepBuilding: 'Costruzione',
  stepOpeningPreview: 'Apertura dell’anteprima',
  stepCheckingLook: 'Verifica dell’aspetto',
  groupBuilt: 'App creata',
  groupUpdated: 'App aggiornata',
  groupBuiltDeck: 'Presentazione creata',
  groupUpdatedDeck: 'Presentazione aggiornata',
  groupChecked: 'Aspetto verificato',
  groupLookedAt: 'Consultato: {domain}',
  groupExplored: 'Progetto esplorato',
  stepsNoun: 'one:passaggio|other:passaggi',
  failedStepsNoun: 'one:passaggio non ha funzionato|other:passaggi non hanno funzionato',
  cardBuilding: 'Costruzione…',
  cardUpdating: 'Aggiornamento…',
  cardUpdatedJustNow: 'Aggiornata ora',
  notMounted:
    'Questa versione non ha mostrato nulla: il suo codice non avvia ancora l’app nella pagina. La chat può correggerlo.',
  yourApp: 'La tua App',
  yourDeck: 'La tua presentazione',
  tabApp: 'App',
  tabActivity: 'Attività',
  openFullPage: 'Apri a pagina intera',
  selectToggle: 'Seleziona',
  selectHint: 'Fai clic su una parte dell’App per modificarla in chat · Esc per uscire',
  selectedText: 'Selezionato: «{text}»',
  selectedPart: 'Selezionato: una parte dell’App',
  removeSelection: 'Rimuovi la selezione',
  buildingHint: 'La tua App appare qui appena viene creata.',
  deckExport: 'Esporta',
  deckExporting: 'Esportazione…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Ogni slide come una pagina, esattamente come progettata.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Ogni slide come immagine, con le note del relatore. Il testo non è modificabile.',
  deckExportFailed: 'Impossibile esportare la presentazione. Riprova.',
};
const pt: Copy = {
  stepListing: 'A analisar os ficheiros',
  stepReading: 'A ler um ficheiro',
  stepWriting: 'A escrever um ficheiro',
  stepRunning: 'A executar um comando',
  stepBuilding: 'A construir',
  stepOpeningPreview: 'A abrir a pré-visualização',
  stepCheckingLook: 'A verificar o aspeto',
  groupBuilt: 'App criada',
  groupUpdated: 'App atualizada',
  groupBuiltDeck: 'Apresentação criada',
  groupUpdatedDeck: 'Apresentação atualizada',
  groupChecked: 'Aspeto verificado',
  groupLookedAt: 'Consultado: {domain}',
  groupExplored: 'Projeto explorado',
  stepsNoun: 'one:passo|many:de passos|other:passos',
  failedStepsNoun:
    'one:passo não funcionou|many:de passos não funcionaram|other:passos não funcionaram',
  cardBuilding: 'A construir…',
  cardUpdating: 'A atualizar…',
  cardUpdatedJustNow: 'Atualizada agora mesmo',
  notMounted:
    'Esta versão não mostrou nada: o código ainda não inicia a app na página. O chat pode corrigir isto.',
  yourApp: 'A sua App',
  yourDeck: 'A sua apresentação',
  tabApp: 'App',
  tabActivity: 'Atividade',
  openFullPage: 'Abrir em página inteira',
  selectToggle: 'Selecionar',
  selectHint: 'Clique numa parte da App para a alterar no chat · Esc para sair',
  selectedText: 'Selecionado: «{text}»',
  selectedPart: 'Selecionado: uma parte da App',
  removeSelection: 'Remover a seleção',
  buildingHint: 'A sua App aparece aqui assim que for criada.',
  deckExport: 'Exportar',
  deckExporting: 'A exportar…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Cada diapositivo como uma página, exatamente como foi desenhado.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Cada diapositivo como imagem, com as notas do orador. O texto não é editável.',
  deckExportFailed: 'Não foi possível exportar a apresentação. Tente novamente.',
};
const nl: Copy = {
  stepListing: 'Bestanden doornemen',
  stepReading: 'Bestand lezen',
  stepWriting: 'Bestand schrijven',
  stepRunning: 'Opdracht uitvoeren',
  stepBuilding: 'Bouwen',
  stepOpeningPreview: 'Voorbeeld openen',
  stepCheckingLook: 'Controleren hoe het eruitziet',
  groupBuilt: 'App gebouwd',
  groupUpdated: 'App bijgewerkt',
  groupBuiltDeck: 'Presentatie gebouwd',
  groupUpdatedDeck: 'Presentatie bijgewerkt',
  groupChecked: 'Gecontroleerd hoe het eruitziet',
  groupLookedAt: 'Bekeken: {domain}',
  groupExplored: 'Project verkend',
  stepsNoun: 'one:stap|other:stappen',
  failedStepsNoun: 'one:stap werkte niet|other:stappen werkten niet',
  cardBuilding: 'Bouwen…',
  cardUpdating: 'Bijwerken…',
  cardUpdatedJustNow: 'Zojuist bijgewerkt',
  notMounted:
    'Deze versie liet niets zien: de code start de app nog niet op de pagina. De chat kan dit oplossen.',
  yourApp: 'Je app',
  yourDeck: 'Je presentatie',
  tabApp: 'App',
  tabActivity: 'Activiteit',
  openFullPage: 'Volledige pagina openen',
  selectToggle: 'Selecteren',
  selectHint: 'Klik op een onderdeel van de app om het in de chat te wijzigen · Esc om te stoppen',
  selectedText: 'Geselecteerd: ‘{text}’',
  selectedPart: 'Geselecteerd: een onderdeel van de app',
  removeSelection: 'Selectie verwijderen',
  buildingHint: 'Je app verschijnt hier zodra hij gebouwd is.',
  deckExport: 'Exporteren',
  deckExporting: 'Exporteren…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Elke dia als een pagina, precies zoals ontworpen.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Elke dia als afbeelding, met de sprekersnotities. De tekst is niet bewerkbaar.',
  deckExportFailed: 'De presentatie kon niet worden geëxporteerd. Probeer het opnieuw.',
};
const fi: Copy = {
  stepListing: 'Käydään läpi tiedostoja',
  stepReading: 'Luetaan tiedostoa',
  stepWriting: 'Kirjoitetaan tiedostoa',
  stepRunning: 'Suoritetaan komentoa',
  stepBuilding: 'Rakennetaan',
  stepOpeningPreview: 'Avataan esikatselua',
  stepCheckingLook: 'Tarkistetaan, miltä se näyttää',
  groupBuilt: 'Sovellus rakennettu',
  groupUpdated: 'Sovellus päivitetty',
  groupBuiltDeck: 'Esitys rakennettu',
  groupUpdatedDeck: 'Esitys päivitetty',
  groupChecked: 'Tarkistettu, miltä se näyttää',
  groupLookedAt: 'Katsottu: {domain}',
  groupExplored: 'Projekti tutkittu',
  stepsNoun: 'one:vaihe|other:vaihetta',
  failedStepsNoun: 'one:vaihe ei toiminut|other:vaihetta ei toiminut',
  cardBuilding: 'Rakennetaan…',
  cardUpdating: 'Päivitetään…',
  cardUpdatedJustNow: 'Päivitetty juuri nyt',
  notMounted:
    'Tämä versio ei näyttänyt mitään: sen koodi ei vielä käynnistä sovellusta sivulla. Chat voi korjata sen.',
  yourApp: 'Sovelluksesi',
  yourDeck: 'Esityksesi',
  tabApp: 'Sovellus',
  tabActivity: 'Toiminta',
  openFullPage: 'Avaa koko sivu',
  selectToggle: 'Valitse',
  selectHint: 'Napsauta sovelluksen osaa muuttaaksesi sitä chatissa · Esc lopettaa',
  selectedText: 'Valittu: ”{text}”',
  selectedPart: 'Valittu: sovelluksen osa',
  removeSelection: 'Poista valinta',
  buildingHint: 'Sovelluksesi näkyy tässä heti, kun se on rakennettu.',
  deckExport: 'Vie',
  deckExporting: 'Viedään…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Jokainen dia omana sivunaan, juuri kuten se on suunniteltu.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Jokainen dia kuvana puhujan muistiinpanoineen. Tekstiä ei voi muokata.',
  deckExportFailed: 'Esityksen vienti epäonnistui. Yritä uudelleen.',
};
const et: Copy = {
  stepListing: 'Vaatan faile läbi',
  stepReading: 'Loen faili',
  stepWriting: 'Kirjutan faili',
  stepRunning: 'Käivitan käsu',
  stepBuilding: 'Ehitamine',
  stepOpeningPreview: 'Avan eelvaate',
  stepCheckingLook: 'Kontrollin, kuidas see välja näeb',
  groupBuilt: 'Rakendus ehitatud',
  groupUpdated: 'Rakendus uuendatud',
  groupBuiltDeck: 'Esitlus ehitatud',
  groupUpdatedDeck: 'Esitlus uuendatud',
  groupChecked: 'Kontrollitud, kuidas see välja näeb',
  groupLookedAt: 'Vaadatud: {domain}',
  groupExplored: 'Projekt uuritud',
  stepsNoun: 'one:samm|other:sammu',
  failedStepsNoun: 'one:samm ei töötanud|other:sammu ei töötanud',
  cardBuilding: 'Ehitamine…',
  cardUpdating: 'Uuendamine…',
  cardUpdatedJustNow: 'Just uuendatud',
  notMounted:
    'See versioon ei näidanud midagi: selle kood ei käivita rakendust veel lehel. Vestlus saab selle parandada.',
  yourApp: 'Sinu rakendus',
  yourDeck: 'Sinu esitlus',
  tabApp: 'Rakendus',
  tabActivity: 'Tegevus',
  openFullPage: 'Ava täislehena',
  selectToggle: 'Vali',
  selectHint: 'Klõpsa rakenduse osal, et seda vestluses muuta · Esc lõpetamiseks',
  selectedText: 'Valitud: „{text}“',
  selectedPart: 'Valitud: rakenduse osa',
  removeSelection: 'Eemalda valik',
  buildingHint: 'Sinu rakendus ilmub siia kohe, kui see on valmis.',
  deckExport: 'Ekspordi',
  deckExporting: 'Eksportimine…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Iga slaid eraldi lehena, täpselt nagu kujundatud.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Iga slaid pildina koos esineja märkmetega. Teksti ei saa muuta.',
  deckExportFailed: 'Esitlust ei õnnestunud eksportida. Proovi uuesti.',
};
const bg: Copy = {
  stepListing: 'Преглеждам файловете',
  stepReading: 'Чета файл',
  stepWriting: 'Записвам файл',
  stepRunning: 'Изпълнявам команда',
  stepBuilding: 'Изграждане',
  stepOpeningPreview: 'Отварям прегледа',
  stepCheckingLook: 'Проверявам как изглежда',
  groupBuilt: 'Приложението е изградено',
  groupUpdated: 'Приложението е обновено',
  groupBuiltDeck: 'Презентацията е изградена',
  groupUpdatedDeck: 'Презентацията е обновена',
  groupChecked: 'Проверено как изглежда',
  groupLookedAt: 'Разгледан: {domain}',
  groupExplored: 'Проектът е разгледан',
  stepsNoun: 'one:стъпка|other:стъпки',
  failedStepsNoun: 'one:стъпка не сработи|other:стъпки не сработиха',
  cardBuilding: 'Изграждане…',
  cardUpdating: 'Обновяване…',
  cardUpdatedJustNow: 'Обновено току-що',
  notMounted:
    'Тази версия не показа нищо: кодът ѝ още не стартира приложението на страницата. Чатът може да го поправи.',
  yourApp: 'Твоето приложение',
  yourDeck: 'Твоята презентация',
  tabApp: 'Приложение',
  tabActivity: 'Дейност',
  openFullPage: 'Отваряне на цяла страница',
  selectToggle: 'Избор',
  selectHint: 'Щракни върху част от приложението, за да я промениш в чата · Esc за спиране',
  selectedText: 'Избрано: „{text}“',
  selectedPart: 'Избрано: част от приложението',
  removeSelection: 'Премахни избора',
  buildingHint: 'Приложението ти ще се появи тук веднага щом бъде изградено.',
  deckExport: 'Експорт',
  deckExporting: 'Експортиране…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Всеки слайд като страница, точно както е оформен.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Всеки слайд като изображение, с бележките на говорещия. Текстът не може да се редактира.',
  deckExportFailed: 'Презентацията не можа да бъде експортирана. Опитай отново.',
};
const cs: Copy = {
  stepListing: 'Procházím soubory',
  stepReading: 'Čtu soubor',
  stepWriting: 'Zapisuji soubor',
  stepRunning: 'Spouštím příkaz',
  stepBuilding: 'Sestavování',
  stepOpeningPreview: 'Otevírám náhled',
  stepCheckingLook: 'Kontroluji, jak to vypadá',
  groupBuilt: 'Aplikace sestavena',
  groupUpdated: 'Aplikace aktualizována',
  groupBuiltDeck: 'Prezentace sestavena',
  groupUpdatedDeck: 'Prezentace aktualizována',
  groupChecked: 'Zkontrolováno, jak to vypadá',
  groupLookedAt: 'Zobrazeno: {domain}',
  groupExplored: 'Projekt prozkoumán',
  stepsNoun: 'one:krok|few:kroky|many:kroku|other:kroků',
  failedStepsNoun:
    'one:krok nefungoval|few:kroky nefungovaly|many:kroku nefungovalo|other:kroků nefungovalo',
  cardBuilding: 'Sestavování…',
  cardUpdating: 'Aktualizace…',
  cardUpdatedJustNow: 'Právě aktualizováno',
  notMounted:
    'Tato verze nic nezobrazila: její kód zatím aplikaci na stránce nespouští. Chat to může opravit.',
  yourApp: 'Vaše aplikace',
  yourDeck: 'Vaše prezentace',
  tabApp: 'Aplikace',
  tabActivity: 'Aktivita',
  openFullPage: 'Otevřít na celé stránce',
  selectToggle: 'Vybrat',
  selectHint: 'Klikněte na část aplikace a změňte ji v chatu · Esc pro ukončení',
  selectedText: 'Vybráno: „{text}“',
  selectedPart: 'Vybráno: část aplikace',
  removeSelection: 'Odebrat výběr',
  buildingHint: 'Vaše aplikace se tu zobrazí, jakmile bude sestavena.',
  deckExport: 'Exportovat',
  deckExporting: 'Exportuje se…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Každý snímek jako stránka, přesně podle návrhu.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Každý snímek jako obrázek s poznámkami řečníka. Text nelze upravovat.',
  deckExportFailed: 'Prezentaci se nepodařilo exportovat. Zkuste to znovu.',
};
const sk: Copy = {
  stepListing: 'Prezerám súbory',
  stepReading: 'Čítam súbor',
  stepWriting: 'Zapisujem súbor',
  stepRunning: 'Spúšťam príkaz',
  stepBuilding: 'Zostavovanie',
  stepOpeningPreview: 'Otváram náhľad',
  stepCheckingLook: 'Kontrolujem, ako to vyzerá',
  groupBuilt: 'Aplikácia zostavená',
  groupUpdated: 'Aplikácia aktualizovaná',
  groupBuiltDeck: 'Prezentácia zostavená',
  groupUpdatedDeck: 'Prezentácia aktualizovaná',
  groupChecked: 'Skontrolované, ako to vyzerá',
  groupLookedAt: 'Zobrazené: {domain}',
  groupExplored: 'Projekt preskúmaný',
  stepsNoun: 'one:krok|few:kroky|many:kroku|other:krokov',
  failedStepsNoun:
    'one:krok nefungoval|few:kroky nefungovali|many:kroku nefungovalo|other:krokov nefungovalo',
  cardBuilding: 'Zostavovanie…',
  cardUpdating: 'Aktualizácia…',
  cardUpdatedJustNow: 'Práve aktualizované',
  notMounted:
    'Táto verzia nič nezobrazila: jej kód zatiaľ nespúšťa aplikáciu na stránke. Chat to môže opraviť.',
  yourApp: 'Vaša aplikácia',
  yourDeck: 'Vaša prezentácia',
  tabApp: 'Aplikácia',
  tabActivity: 'Aktivita',
  openFullPage: 'Otvoriť na celej stránke',
  selectToggle: 'Vybrať',
  selectHint: 'Kliknite na časť aplikácie a zmeňte ju v chate · Esc na ukončenie',
  selectedText: 'Vybrané: „{text}“',
  selectedPart: 'Vybrané: časť aplikácie',
  removeSelection: 'Odstrániť výber',
  buildingHint: 'Vaša aplikácia sa tu zobrazí hneď, ako bude zostavená.',
  deckExport: 'Exportovať',
  deckExporting: 'Exportuje sa…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Každá snímka ako strana, presne podľa návrhu.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Každá snímka ako obrázok s poznámkami rečníka. Text sa nedá upravovať.',
  deckExportFailed: 'Prezentáciu sa nepodarilo exportovať. Skúste to znova.',
};
const pl: Copy = {
  stepListing: 'Przeglądanie plików',
  stepReading: 'Czytanie pliku',
  stepWriting: 'Zapisywanie pliku',
  stepRunning: 'Uruchamianie polecenia',
  stepBuilding: 'Budowanie',
  stepOpeningPreview: 'Otwieranie podglądu',
  stepCheckingLook: 'Sprawdzanie wyglądu',
  groupBuilt: 'Zbudowano aplikację',
  groupUpdated: 'Zaktualizowano aplikację',
  groupBuiltDeck: 'Zbudowano prezentację',
  groupUpdatedDeck: 'Zaktualizowano prezentację',
  groupChecked: 'Sprawdzono wygląd',
  groupLookedAt: 'Przejrzano: {domain}',
  groupExplored: 'Przejrzano projekt',
  stepsNoun: 'one:krok|few:kroki|many:kroków|other:kroku',
  failedStepsNoun:
    'one:krok nie zadziałał|few:kroki nie zadziałały|many:kroków nie zadziałało|other:kroku nie zadziałało',
  cardBuilding: 'Budowanie…',
  cardUpdating: 'Aktualizowanie…',
  cardUpdatedJustNow: 'Zaktualizowano przed chwilą',
  notMounted:
    'Ta wersja niczego nie pokazała: jej kod nie uruchamia jeszcze aplikacji na stronie. Czat może to naprawić.',
  yourApp: 'Twoja aplikacja',
  yourDeck: 'Twoja prezentacja',
  tabApp: 'Aplikacja',
  tabActivity: 'Aktywność',
  openFullPage: 'Otwórz na całej stronie',
  selectToggle: 'Zaznacz',
  selectHint: 'Kliknij część aplikacji, aby zmienić ją na czacie · Esc, aby zakończyć',
  selectedText: 'Zaznaczono: „{text}”',
  selectedPart: 'Zaznaczono: część aplikacji',
  removeSelection: 'Usuń zaznaczenie',
  buildingHint: 'Twoja aplikacja pojawi się tutaj, gdy tylko zostanie zbudowana.',
  deckExport: 'Eksportuj',
  deckExporting: 'Eksportowanie…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Każdy slajd jako strona, dokładnie tak, jak zaprojektowano.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Każdy slajd jako obraz, z notatkami prelegenta. Tekstu nie można edytować.',
  deckExportFailed: 'Nie udało się wyeksportować prezentacji. Spróbuj ponownie.',
};
const hr: Copy = {
  stepListing: 'Pregledavam datoteke',
  stepReading: 'Čitam datoteku',
  stepWriting: 'Pišem datoteku',
  stepRunning: 'Pokrećem naredbu',
  stepBuilding: 'Izrada',
  stepOpeningPreview: 'Otvaram pretpregled',
  stepCheckingLook: 'Provjeravam kako izgleda',
  groupBuilt: 'Aplikacija izrađena',
  groupUpdated: 'Aplikacija ažurirana',
  groupBuiltDeck: 'Prezentacija izrađena',
  groupUpdatedDeck: 'Prezentacija ažurirana',
  groupChecked: 'Provjereno kako izgleda',
  groupLookedAt: 'Pregledano: {domain}',
  groupExplored: 'Projekt istražen',
  stepsNoun: 'one:korak|few:koraka|other:koraka',
  failedStepsNoun: 'one:korak nije uspio|few:koraka nije uspjelo|other:koraka nije uspjelo',
  cardBuilding: 'Izrada…',
  cardUpdating: 'Ažuriranje…',
  cardUpdatedJustNow: 'Upravo ažurirano',
  notMounted:
    'Ova verzija nije ništa prikazala: njezin kod još ne pokreće aplikaciju na stranici. Chat to može popraviti.',
  yourApp: 'Tvoja aplikacija',
  yourDeck: 'Tvoja prezentacija',
  tabApp: 'Aplikacija',
  tabActivity: 'Aktivnost',
  openFullPage: 'Otvori na cijeloj stranici',
  selectToggle: 'Odaberi',
  selectHint: 'Kliknite dio aplikacije da biste ga promijenili u chatu · Esc za kraj',
  selectedText: 'Odabrano: „{text}”',
  selectedPart: 'Odabrano: dio aplikacije',
  removeSelection: 'Ukloni odabir',
  buildingHint: 'Tvoja aplikacija pojavit će se ovdje čim bude izgrađena.',
  deckExport: 'Izvezi',
  deckExporting: 'Izvoz…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Svaki slajd kao stranica, točno kako je oblikovan.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Svaki slajd kao slika, s bilješkama govornika. Tekst se ne može uređivati.',
  deckExportFailed: 'Prezentaciju nije bilo moguće izvesti. Pokušaj ponovno.',
};
const sl: Copy = {
  stepListing: 'Pregledovanje datotek',
  stepReading: 'Branje datoteke',
  stepWriting: 'Pisanje datoteke',
  stepRunning: 'Zaganjanje ukaza',
  stepBuilding: 'Gradnja',
  stepOpeningPreview: 'Odpiranje predogleda',
  stepCheckingLook: 'Preverjanje videza',
  groupBuilt: 'Aplikacija zgrajena',
  groupUpdated: 'Aplikacija posodobljena',
  groupBuiltDeck: 'Predstavitev zgrajena',
  groupUpdatedDeck: 'Predstavitev posodobljena',
  groupChecked: 'Videz preverjen',
  groupLookedAt: 'Ogledano: {domain}',
  groupExplored: 'Projekt raziskan',
  stepsNoun: 'one:korak|two:koraka|few:koraki|other:korakov',
  failedStepsNoun:
    'one:korak ni uspel|two:koraka nista uspela|few:koraki niso uspeli|other:korakov ni uspelo',
  cardBuilding: 'Gradnja…',
  cardUpdating: 'Posodabljanje…',
  cardUpdatedJustNow: 'Pravkar posodobljeno',
  notMounted:
    'Ta različica ni prikazala ničesar: njena koda še ne zažene aplikacije na strani. Klepet lahko to popravi.',
  yourApp: 'Tvoja aplikacija',
  yourDeck: 'Tvoja predstavitev',
  tabApp: 'Aplikacija',
  tabActivity: 'Dejavnost',
  openFullPage: 'Odpri na celotni strani',
  selectToggle: 'Izberi',
  selectHint: 'Kliknite del aplikacije, da ga spremenite v klepetu · Esc za konec',
  selectedText: 'Izbrano: »{text}«',
  selectedPart: 'Izbrano: del aplikacije',
  removeSelection: 'Odstrani izbor',
  buildingHint: 'Tvoja aplikacija se bo prikazala tukaj, ko bo zgrajena.',
  deckExport: 'Izvozi',
  deckExporting: 'Izvažanje…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Vsak diapozitiv kot stran, natanko tak, kot je oblikovan.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Vsak diapozitiv kot slika z govornikovimi opombami. Besedila ni mogoče urejati.',
  deckExportFailed: 'Predstavitve ni bilo mogoče izvoziti. Poskusi znova.',
};
const hu: Copy = {
  stepListing: 'Fájlok áttekintése',
  stepReading: 'Fájl olvasása',
  stepWriting: 'Fájl írása',
  stepRunning: 'Parancs futtatása',
  stepBuilding: 'Építés',
  stepOpeningPreview: 'Előnézet megnyitása',
  stepCheckingLook: 'Megjelenés ellenőrzése',
  groupBuilt: 'Alkalmazás elkészítve',
  groupUpdated: 'Alkalmazás frissítve',
  groupBuiltDeck: 'Prezentáció elkészítve',
  groupUpdatedDeck: 'Prezentáció frissítve',
  groupChecked: 'Megjelenés ellenőrizve',
  groupLookedAt: 'Megtekintve: {domain}',
  groupExplored: 'Projekt átnézve',
  stepsNoun: 'one:lépés|other:lépés',
  failedStepsNoun: 'one:lépés nem sikerült|other:lépés nem sikerült',
  cardBuilding: 'Építés…',
  cardUpdating: 'Frissítés…',
  cardUpdatedJustNow: 'Épp most frissítve',
  notMounted:
    'Ez a verzió nem jelenített meg semmit: a kódja még nem indítja el az alkalmazást az oldalon. A csevegés ki tudja javítani.',
  yourApp: 'Az alkalmazásod',
  yourDeck: 'A prezentációd',
  tabApp: 'Alkalmazás',
  tabActivity: 'Tevékenység',
  openFullPage: 'Megnyitás teljes oldalon',
  selectToggle: 'Kijelölés',
  selectHint: 'Kattints az alkalmazás egy részére, hogy a chatben módosítsd · Esc a befejezéshez',
  selectedText: 'Kijelölve: „{text}”',
  selectedPart: 'Kijelölve: az alkalmazás egy része',
  removeSelection: 'Kijelölés törlése',
  buildingHint: 'Az alkalmazásod itt jelenik meg, amint elkészül.',
  deckExport: 'Exportálás',
  deckExporting: 'Exportálás…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Minden dia külön oldalon, pontosan úgy, ahogy megtervezték.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Minden dia képként, az előadói jegyzetekkel. A szöveg nem szerkeszthető.',
  deckExportFailed: 'A bemutatót nem sikerült exportálni. Próbáld újra.',
};
const el: Copy = {
  stepListing: 'Εξέταση αρχείων',
  stepReading: 'Ανάγνωση αρχείου',
  stepWriting: 'Εγγραφή αρχείου',
  stepRunning: 'Εκτέλεση εντολής',
  stepBuilding: 'Δόμηση',
  stepOpeningPreview: 'Άνοιγμα της προεπισκόπησης',
  stepCheckingLook: 'Έλεγχος της εμφάνισης',
  groupBuilt: 'Η εφαρμογή δημιουργήθηκε',
  groupUpdated: 'Η εφαρμογή ενημερώθηκε',
  groupBuiltDeck: 'Η παρουσίαση δημιουργήθηκε',
  groupUpdatedDeck: 'Η παρουσίαση ενημερώθηκε',
  groupChecked: 'Έγινε έλεγχος της εμφάνισης',
  groupLookedAt: 'Προβλήθηκε: {domain}',
  groupExplored: 'Το έργο εξερευνήθηκε',
  stepsNoun: 'one:βήμα|other:βήματα',
  failedStepsNoun: 'one:βήμα δεν λειτούργησε|other:βήματα δεν λειτούργησαν',
  cardBuilding: 'Δόμηση…',
  cardUpdating: 'Ενημέρωση…',
  cardUpdatedJustNow: 'Ενημερώθηκε μόλις τώρα',
  notMounted:
    'Αυτή η έκδοση δεν εμφάνισε τίποτα: ο κώδικάς της δεν ξεκινά ακόμα την εφαρμογή στη σελίδα. Η συνομιλία μπορεί να το διορθώσει.',
  yourApp: 'Η εφαρμογή σας',
  yourDeck: 'Η παρουσίασή σας',
  tabApp: 'Εφαρμογή',
  tabActivity: 'Δραστηριότητα',
  openFullPage: 'Άνοιγμα σε πλήρη σελίδα',
  selectToggle: 'Επιλογή',
  selectHint:
    'Κάντε κλικ σε ένα τμήμα της εφαρμογής για να το αλλάξετε στη συνομιλία · Esc για τερματισμό',
  selectedText: 'Επιλέχθηκε: «{text}»',
  selectedPart: 'Επιλέχθηκε: ένα τμήμα της εφαρμογής',
  removeSelection: 'Κατάργηση επιλογής',
  buildingHint: 'Η εφαρμογή σας εμφανίζεται εδώ μόλις δημιουργηθεί.',
  deckExport: 'Εξαγωγή',
  deckExporting: 'Εξαγωγή…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Κάθε διαφάνεια ως σελίδα, ακριβώς όπως σχεδιάστηκε.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Κάθε διαφάνεια ως εικόνα, με τις σημειώσεις ομιλητή. Το κείμενο δεν είναι επεξεργάσιμο.',
  deckExportFailed: 'Δεν ήταν δυνατή η εξαγωγή της παρουσίασης. Δοκιμάστε ξανά.',
};
const ro: Copy = {
  stepListing: 'Se analizează fișierele',
  stepReading: 'Se citește un fișier',
  stepWriting: 'Se scrie un fișier',
  stepRunning: 'Se rulează o comandă',
  stepBuilding: 'Se construiește',
  stepOpeningPreview: 'Se deschide previzualizarea',
  stepCheckingLook: 'Se verifică aspectul',
  groupBuilt: 'Aplicația a fost construită',
  groupUpdated: 'Aplicația a fost actualizată',
  groupBuiltDeck: 'Prezentarea a fost construită',
  groupUpdatedDeck: 'Prezentarea a fost actualizată',
  groupChecked: 'Aspect verificat',
  groupLookedAt: 'Vizualizat: {domain}',
  groupExplored: 'Proiectul a fost explorat',
  stepsNoun: 'one:pas|few:pași|other:de pași',
  failedStepsNoun:
    'one:pas nu a funcționat|few:pași nu au funcționat|other:de pași nu au funcționat',
  cardBuilding: 'Se construiește…',
  cardUpdating: 'Se actualizează…',
  cardUpdatedJustNow: 'Actualizată chiar acum',
  notMounted:
    'Această versiune nu a afișat nimic: codul ei încă nu pornește aplicația pe pagină. Conversația poate repara asta.',
  yourApp: 'Aplicația ta',
  yourDeck: 'Prezentarea ta',
  tabApp: 'Aplicație',
  tabActivity: 'Activitate',
  openFullPage: 'Deschide pe toată pagina',
  selectToggle: 'Selectează',
  selectHint: 'Fă clic pe o parte a aplicației pentru a o schimba în chat · Esc pentru a opri',
  selectedText: 'Selectat: „{text}”',
  selectedPart: 'Selectat: o parte a aplicației',
  removeSelection: 'Elimină selecția',
  buildingHint: 'Aplicația ta apare aici imediat ce este construită.',
  deckExport: 'Exportă',
  deckExporting: 'Se exportă…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Fiecare diapozitiv ca pagină, exact cum a fost conceput.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Fiecare diapozitiv ca imagine, cu notele vorbitorului. Textul nu poate fi editat.',
  deckExportFailed: 'Prezentarea nu a putut fi exportată. Încearcă din nou.',
};
const lt: Copy = {
  stepListing: 'Peržiūrimi failai',
  stepReading: 'Skaitomas failas',
  stepWriting: 'Rašomas failas',
  stepRunning: 'Vykdoma komanda',
  stepBuilding: 'Kuriama',
  stepOpeningPreview: 'Atidaroma peržiūra',
  stepCheckingLook: 'Tikrinama, kaip atrodo',
  groupBuilt: 'Programėlė sukurta',
  groupUpdated: 'Programėlė atnaujinta',
  groupBuiltDeck: 'Pristatymas sukurtas',
  groupUpdatedDeck: 'Pristatymas atnaujintas',
  groupChecked: 'Patikrinta, kaip atrodo',
  groupLookedAt: 'Peržiūrėta: {domain}',
  groupExplored: 'Projektas peržiūrėtas',
  stepsNoun: 'one:žingsnis|few:žingsniai|many:žingsnio|other:žingsnių',
  failedStepsNoun:
    'one:žingsnis nepavyko|few:žingsniai nepavyko|many:žingsnio nepavyko|other:žingsnių nepavyko',
  cardBuilding: 'Kuriama…',
  cardUpdating: 'Atnaujinama…',
  cardUpdatedJustNow: 'Ką tik atnaujinta',
  notMounted:
    'Ši versija nieko neparodė: jos kodas dar nepaleidžia programėlės puslapyje. Pokalbis gali tai pataisyti.',
  yourApp: 'Jūsų programėlė',
  yourDeck: 'Jūsų pristatymas',
  tabApp: 'Programėlė',
  tabActivity: 'Veikla',
  openFullPage: 'Atidaryti visame puslapyje',
  selectToggle: 'Pasirinkti',
  selectHint: 'Spustelėkite programėlės dalį, kad ją pakeistumėte pokalbyje · Esc – baigti',
  selectedText: 'Pasirinkta: „{text}“',
  selectedPart: 'Pasirinkta: programėlės dalis',
  removeSelection: 'Pašalinti pasirinkimą',
  buildingHint: 'Jūsų programėlė pasirodys čia, kai tik bus sukurta.',
  deckExport: 'Eksportuoti',
  deckExporting: 'Eksportuojama…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Kiekviena skaidrė kaip puslapis, tiksliai kaip sukurta.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Kiekviena skaidrė kaip paveikslėlis su pranešėjo pastabomis. Teksto redaguoti negalima.',
  deckExportFailed: 'Nepavyko eksportuoti pristatymo. Bandykite dar kartą.',
};
const lv: Copy = {
  stepListing: 'Pārskata failus',
  stepReading: 'Lasa failu',
  stepWriting: 'Raksta failu',
  stepRunning: 'Izpilda komandu',
  stepBuilding: 'Būvē',
  stepOpeningPreview: 'Atver priekšskatījumu',
  stepCheckingLook: 'Pārbauda, kā tas izskatās',
  groupBuilt: 'Lietotne izveidota',
  groupUpdated: 'Lietotne atjaunināta',
  groupBuiltDeck: 'Prezentācija izveidota',
  groupUpdatedDeck: 'Prezentācija atjaunināta',
  groupChecked: 'Pārbaudīts, kā tas izskatās',
  groupLookedAt: 'Apskatīts: {domain}',
  groupExplored: 'Projekts izpētīts',
  stepsNoun: 'zero:soļu|one:solis|other:soļi',
  failedStepsNoun: 'zero:soļu neizdevās|one:solis neizdevās|other:soļi neizdevās',
  cardBuilding: 'Būvē…',
  cardUpdating: 'Atjaunina…',
  cardUpdatedJustNow: 'Tikko atjaunināta',
  notMounted:
    'Šī versija neko neparādīja: tās kods vēl nepalaiž lietotni lapā. Tērzēšana to var izlabot.',
  yourApp: 'Tava lietotne',
  yourDeck: 'Tava prezentācija',
  tabApp: 'Lietotne',
  tabActivity: 'Darbība',
  openFullPage: 'Atvērt pilnā lapā',
  selectToggle: 'Atlasīt',
  selectHint: 'Noklikšķiniet uz lietotnes daļas, lai to mainītu tērzēšanā · Esc, lai beigtu',
  selectedText: 'Atlasīts: “{text}”',
  selectedPart: 'Atlasīts: lietotnes daļa',
  removeSelection: 'Noņemt atlasi',
  buildingHint: 'Tava lietotne parādīsies šeit, tiklīdz tā būs izveidota.',
  deckExport: 'Eksportēt',
  deckExporting: 'Eksportē…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Katrs slaids kā lappuse, tieši tā, kā veidots.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint: 'Katrs slaids kā attēls ar runātāja piezīmēm. Tekstu nevar rediģēt.',
  deckExportFailed: 'Prezentāciju neizdevās eksportēt. Mēģiniet vēlreiz.',
};
const ga: Copy = {
  stepListing: 'Comhaid á n-iniúchadh',
  stepReading: 'Comhad á léamh',
  stepWriting: 'Comhad á scríobh',
  stepRunning: 'Ordú á rith',
  stepBuilding: 'Á tógáil',
  stepOpeningPreview: 'An réamhamharc á oscailt',
  stepCheckingLook: 'An chuma á seiceáil',
  groupBuilt: 'Tógadh an aip',
  groupUpdated: 'Nuashonraíodh an aip',
  groupBuiltDeck: 'Tógadh an láithreoireacht',
  groupUpdatedDeck: 'Nuashonraíodh an láithreoireacht',
  groupChecked: 'Cuma seiceáilte',
  groupLookedAt: 'Breathnaíodh ar: {domain}',
  groupExplored: 'Iniúchadh an tionscadal',
  stepsNoun: 'one:céim|two:chéim|few:chéim|many:gcéim|other:céim',
  failedStepsNoun:
    'one:céim nár oibrigh|two:chéim nár oibrigh|few:chéim nár oibrigh|many:gcéim nár oibrigh|other:céim nár oibrigh',
  cardBuilding: 'Á tógáil…',
  cardUpdating: 'Á nuashonrú…',
  cardUpdatedJustNow: 'Nuashonraithe anois díreach',
  notMounted:
    'Níor thaispeáin an leagan seo aon rud: ní thosaíonn a chód an aip ar an leathanach fós. Is féidir leis an gcomhrá é a dheisiú.',
  yourApp: 'D’aip',
  yourDeck: 'Do láithreoireacht',
  tabApp: 'Aip',
  tabActivity: 'Gníomhaíocht',
  openFullPage: 'Oscail ar leathanach iomlán',
  selectToggle: 'Roghnaigh',
  selectHint: 'Cliceáil ar chuid den aip chun í a athrú sa chomhrá · Esc chun stopadh',
  selectedText: 'Roghnaithe: “{text}”',
  selectedPart: 'Roghnaithe: cuid den aip',
  removeSelection: 'Bain an roghnúchán',
  buildingHint: 'Taispeánfar d’aip anseo chomh luath agus a bheidh sí tógtha.',
  deckExport: 'Easpórtáil',
  deckExporting: 'Á easpórtáil…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Gach sleamhnán ar leathanach, díreach mar a dearadh é.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Gach sleamhnán mar phictiúr, lena nótaí cainteora. Ní féidir an téacs a chur in eagar.',
  deckExportFailed: 'Níorbh fhéidir an cur i láthair a easpórtáil. Bain triail eile as.',
};
const mt: Copy = {
  stepListing: 'Qed jeżamina l-fajls',
  stepReading: 'Qed jaqra fajl',
  stepWriting: 'Qed jikteb fajl',
  stepRunning: 'Qed iħaddem kmand',
  stepBuilding: 'Qed tinbena',
  stepOpeningPreview: 'Qed jiftaħ il-previżjoni',
  stepCheckingLook: 'Qed jiċċekkja kif tidher',
  groupBuilt: 'L-app inbniet',
  groupUpdated: 'L-app ġiet aġġornata',
  groupBuiltDeck: 'Il-preżentazzjoni nbniet',
  groupUpdatedDeck: 'Il-preżentazzjoni ġiet aġġornata',
  groupChecked: 'Iċċekkjat kif tidher',
  groupLookedAt: 'Ħares lejn: {domain}',
  groupExplored: 'Il-proġett ġie esplorat',
  stepsNoun: 'one:pass|two:passi|few:passi|many:pass|other:pass',
  failedStepsNoun:
    'one:pass ma ħadimx|two:passi ma ħadmux|few:passi ma ħadmux|many:pass ma ħadimx|other:pass ma ħadimx',
  cardBuilding: 'Qed tinbena…',
  cardUpdating: 'Qed tiġi aġġornata…',
  cardUpdatedJustNow: 'Għadha kif ġiet aġġornata',
  notMounted:
    'Din il-verżjoni ma wriet xejn: il-kodiċi tagħha għadu ma jibdiex l-app fil-paġna. Iċ-chat tista’ tirranġaha.',
  yourApp: 'L-app tiegħek',
  yourDeck: 'Il-preżentazzjoni tiegħek',
  tabApp: 'App',
  tabActivity: 'Attività',
  openFullPage: 'Iftaħ f’paġna sħiħa',
  selectToggle: 'Agħżel',
  selectHint: 'Ikklikkja fuq parti mill-app biex tibdilha fiċ-chat · Esc biex tieqaf',
  selectedText: 'Magħżul: “{text}”',
  selectedPart: 'Magħżul: parti mill-app',
  removeSelection: 'Neħħi l-għażla',
  buildingHint: 'L-app tiegħek tidher hawn hekk kif tinbena.',
  deckExport: 'Esporta',
  deckExporting: 'Qed tesporta…',
  deckExportPdf: 'PDF',
  deckExportPdfHint: 'Kull slajd bħala paġna, eżatt kif iddisinjat.',
  deckExportPptx: 'PowerPoint',
  deckExportPptxHint:
    'Kull slajd bħala stampa, bin-noti tal-kelliem. It-test ma jistax jiġi editjat.',
  deckExportFailed: 'Il-preżentazzjoni ma setgħetx tiġi esportata. Erġa’ pprova.',
};

export const buildWatchCopy: NamespaceCatalog<BuildWatchCopyKey> = {
  en,
  sv,
  da,
  de,
  fr,
  es,
  it,
  pt,
  nl,
  fi,
  et,
  bg,
  cs,
  sk,
  pl,
  hr,
  sl,
  hu,
  el,
  ro,
  lt,
  lv,
  ga,
  mt,
};
export default buildWatchCopy;
